import domtoimage from "dom-to-image";
import { Content } from "pdfmake/interfaces";
import getTitle from "report/common/title";

const isLegendCloseIcon = (element: HTMLElement): boolean => {
  return (
    element.classList.contains("material-icons") &&
    element.textContent?.trim() === "cancel"
  );
};

const getLegendCaptureOptions = (element: Element) => {
  const el = element as HTMLElement;
  // 使用 getBoundingClientRect 获取精确尺寸，避免缩放导致的浮点误差
  // 采用 ceil 而非 round 确保容器能容纳所有内容，防止文字换行
  const rect = el.getBoundingClientRect();
  const width = Math.max(1, Math.ceil(rect.width));
  const height = Math.max(1, Math.ceil(rect.height));

  return {
    width,
    height,
    style: {
      width: `${width}px`,
      height: `${height}px`
    },
    cacheBust: true,
    quality: 1
  };
};

const get = async (
  text: string = "Map Legends",
  selector: string = '[class^="agenda_agenda"], [class^="multipleAgenda_multipleAgenda"]',
  selectedShapeId?: string
): Promise<Content[]> => {
  // 查找带有特定类名的元素
  const legendCloseElements = document.querySelectorAll(
    '[class^="agendas_legendClose"]'
  );

  if (!legendCloseElements?.length) {
    return [getTitle(text)];
  }

  const e = legendCloseElements[0];

  // 获取原元素精确的浮点尺寸
  const origRect = e.getBoundingClientRect();

  // 创建元素的克隆
  const clone = e.cloneNode(true) as HTMLElement;

  // 设置克隆元素样式，使其可见但位于视口外
  clone.style.position = "absolute";
  clone.style.left = "-9999px";
  clone.style.top = "0";
  clone.style.display = "block";
  clone.style.visibility = "visible";
  // 采用 ceil 确保克隆元素宽高足以容纳内容，避免90%缩放导致的换行问题
  clone.style.width = `${Math.ceil(origRect.width)}px`;
  clone.style.height = `${Math.ceil(origRect.height)}px`;

  // 添加到文档
  document.body.appendChild(clone);

  // 如果指定了 selectedShapeId，过滤掉其他线段的图例
  if (selectedShapeId) {
    const drawLineLegendsInClone = clone.querySelectorAll(
      '[class^="DrawShapeAgenda_drawShapeAgenda"][data-shape-id], [class^="DrawLineAgenda_drawLineAgenda"][data-polyline-id]'
    );
    drawLineLegendsInClone.forEach((element) => {
      const shapeId =
        element.getAttribute("data-shape-id") ||
        element.getAttribute("data-polyline-id");
      if (shapeId && shapeId !== selectedShapeId) {
        element.remove();
      }
    });
  }

  const elementArray = clone.querySelectorAll(selector);

  const cd = await Promise.all(
    Array.from(elementArray).map(async (e) => {
      return {
        image: await domtoimage.toPng(e, {
          ...getLegendCaptureOptions(e),
          filter: (element: Node) => {
            if (element instanceof HTMLElement) {
              if (isLegendCloseIcon(element)) {
                return false;
              }

              return !Array.from(element.attributes).some(
                (attr) => attr.name === "role" && attr.value === "img"
              );
            }
            if (element instanceof SVGElement) {
              return !Array.from(element.attributes).some(
                (attr) => attr.name === "id" && attr.value === "loc"
              );
            }
            return true;
          }
        }),
        width: 130
      };
    })
  );

  // 移除克隆元素
  document.body.removeChild(clone);

  if (!cd || cd.length == 0) {
    return [];
  }

  if (cd.length <= 3) {
    return [getTitle(text), ...cd];
  }

  return [
    getTitle(text),
    {
      columns: [
        {
          stack: [...cd.slice(0, 3)]
        },
        {
          stack: [...cd.slice(3)]
        }
      ],
      columnGap: 0
    }
  ];
};

export default get;
